/**
 * The Timeline canvas's "Sync" button — pushes the placed cards and their connectors to GitHub.
 *
 * Purely presentational: PlanPage owns the request, the result modal, and every gating decision.
 * This component only knows how to look like the four states it can be in.
 *
 * Rendered as the trailing item of PlanTimelineToolbar, so it does NOT position itself — the
 * toolbar owns placement in the canvas's upper-right corner. Its own wrapper stays for the
 * disabled tooltip, which needs a positioned ancestor a disabled button cannot provide.
 *
 * Icons are hand-written inline SVGs — this codebase has no icon library, matching PlanPage's own
 * TIMELINE_MENU_*_ICON constants.
 */
import React from 'react';

export type SyncButtonState = 'idle' | 'syncing' | 'done' | 'error';

export interface PlanSyncButtonProps {
    disabled: boolean;
    /** Why it is disabled — shown as a tooltip so the user knows where to go to fix it. */
    disabledReason?: string;
    state: SyncButtonState;
    onClick: () => void;
}

/** Two arrows chasing each other in a circle — the conventional "sync" glyph. */
const SYNC_ICON = (
    <svg width='13' height='13' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
        <path d='M13.2 7.1A5.2 5.2 0 0 0 3.9 4.6' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
        <path d='M2.8 8.9a5.2 5.2 0 0 0 9.3 2.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
        <path d='M3.9 1.9v2.8h2.8M12.1 14.1v-2.8H9.3' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
);

const DONE_ICON = (
    <svg width='13' height='13' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
        <path d='M3 8.4l3.2 3.2L13 4.8' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
);

const ERROR_ICON = (
    <svg width='13' height='13' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
        <path d='M8 4.2v4.6' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
        <circle cx='8' cy='11.6' r='1' fill='currentColor' />
    </svg>
);

const STATE_CONTENT: Record<SyncButtonState, { icon: React.ReactNode; label: string }> = {
    idle: { icon: SYNC_ICON, label: 'Sync' },
    syncing: { icon: SYNC_ICON, label: 'Syncing…' },
    done: { icon: DONE_ICON, label: 'Synced' },
    error: { icon: ERROR_ICON, label: 'Sync failed' },
};

export default function PlanSyncButton({ disabled, disabledReason, state, onClick }: PlanSyncButtonProps) {
    const { icon, label } = STATE_CONTENT[state];
    const isBusy = state === 'syncing';
    // A disabled control cannot be hovered in every browser, so the tooltip hangs off the
    // wrapper rather than the button — same reason PlanConnectionRelationshipPopover puts its
    // tooltip on an __item wrapper instead of the button itself.
    const tooltip = disabled ? disabledReason : undefined;

    return (
        <div className='plan-sync' data-tooltip-label={tooltip} data-has-tooltip={tooltip ? 'true' : undefined}>
            <button
                type='button'
                className='plan-sync__btn'
                data-state={state}
                disabled={disabled || isBusy}
                aria-busy={isBusy}
                aria-label={disabled && disabledReason ? `Sync — ${disabledReason}` : label}
                onClick={onClick}
            >
                <span className='plan-sync__icon' data-spin={isBusy ? 'true' : undefined}>{icon}</span>
                <span className='plan-sync__label'>{label}</span>
            </button>
        </div>
    );
}
